import React, { memo, useState } from "react";
import { IconButton, Box, Typography, Tooltip, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import NotesIcon from "@mui/icons-material/Notes";
import color from "../../Constants/colors";
import { useTranslation } from "react-i18next";
import { Features, UserRole } from "../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { ComoInput } from "../Common/ComoInput";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

interface QuestionItemProps {
  question: {
    id: string;
    text: string;
    altText?: string;
  };
  isDirty?: boolean;
  onRemove: () => void;
  onEdit: (newText: string) => void;
  onAltTextEdit: (altText: string) => void;
}

const QuestionItem: React.FC<QuestionItemProps> = memo(
  ({ question, isDirty, onRemove, onEdit, onAltTextEdit }) => {
    const { t } = useTranslation("createQuestionList");
    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(question.text);
    const [showAltText, setShowAltText] = useState(false);
    const [isEditingAltText, setIsEditingAltText] = useState(false);
    const [editedAltText, setEditedAltText] = useState(question.altText || "");
    const { colors: workspace } = useWorkspaceColors();

    const stopDrag = (e: React.PointerEvent) => {
      e.stopPropagation();
    };

    const handleSave = () => {
      onEdit(editedText);
      setIsEditing(false);
    };

    const handleAltTextSave = () => {
      onAltTextEdit(editedAltText);
      setIsEditingAltText(false);
    };

    const handleShowAltText = () => {
      setShowAltText(!showAltText);
      setIsEditingAltText(false);
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: 1,
          px: isDirty ? 1 : 0,
          py: isDirty ? 0.5 : 0,
          transition: "background-color 0.2s ease",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: workspace.primary_dark, mb: 1 }}
            >
              {t("createQuestionList.question")}
            </Typography>
            {isDirty && (
              <Chip
                label={t("createQuestionList.modified", "Modified")}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: color.orange200,
                  color: color.orange600,
                  mb: 1,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 1 }}>
            {isEditing ? (
              <ComoInput
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                fullWidth
              />
            ) : (
              <ComoInput value={editedText} fullWidth disabled />
            )}
            {isEditing ? (
              <Tooltip title={t("createQuestionList.saveQuestion")}>
                <IconButton
                  onPointerDown={stopDrag}
                  onClick={handleSave}
                  sx={{ color: color.green }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t("createQuestionList.editQuestion")}>
                <IconButton
                  onPointerDown={stopDrag}
                  onClick={() => setIsEditing(true)}
                  sx={{ color: color.accent }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {(hasRole(UserRole.ADMIN) ||
              hasPermission(Features.QA_INSIGHTS)) && (
              <Tooltip
                title={
                  showAltText
                    ? t("createQuestionList.hideAltText")
                    : t("createQuestionList.showAltText")
                }
              >
                <IconButton
                  onPointerDown={stopDrag}
                  onClick={handleShowAltText}
                  sx={{ color: color.secondary }}
                >
                  <NotesIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t("createQuestionList.removeQuestion")}>
              <IconButton
                onPointerDown={stopDrag}
                onClick={onRemove}
                sx={{ color: color.red }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {(hasRole(UserRole.ADMIN) || hasPermission(Features.QA_INSIGHTS)) &&
            showAltText && (
              <Box sx={{ mt: 2, marginBottom: "2rem", marginLeft: "1rem" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: color.primary_dark, mb: 1 }}
                >
                  {t("createQuestionList.altText")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ComoInput
                    value={editedAltText}
                    onChange={(e) => setEditedAltText(e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!isEditingAltText}
                    multiline
                    rows={3}
                  />
                  {isEditingAltText ? (
                    <Tooltip title={t("createQuestionList.saveAltText")}>
                      <IconButton
                        onPointerDown={stopDrag}
                        onClick={handleAltTextSave}
                        sx={{ color: color.green }}
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t("createQuestionList.editAltText")}>
                      <IconButton
                        onPointerDown={stopDrag}
                        onClick={() => setIsEditingAltText(true)}
                        sx={{ color: color.accent }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}
        </Box>
      </Box>
    );
  },
);

QuestionItem.displayName = "QuestionItem";

export default QuestionItem;
